import { useLayoutEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

export interface TimelinePoint {
  id: number;
  name: string;
  color: string;
  /** True UTC instants. */
  nextTime: number;
  prevTime: number | null;
}

interface PointsTimelineProps {
  points: TimelinePoint[];
  /** Server-corrected now, unix seconds. */
  clockNow: number;
  /** The point firing next, drawn as the one to watch. */
  leaderId: number | null;
  /** Inside the leader's last fifteen minutes. */
  urgent: boolean;
  /** "08:00 London" — for tooltips. */
  describe: (p: TimelinePoint) => string;
  nowLabel: string;
  /** Format a countdown, e.g. "1h 20m". */
  formatUntil: (seconds: number) => string;
  /**
   * The instrument's stored-clock offset.
   *
   * The axis is labelled on the broker clock, not the viewer's. Those differ
   * by hours, and labelling in a timezone the charts never show means the day
   * on screen is not the trading day.
   */
  offsetSec: number;
}

/** Hour labels are spaced at least this far apart, in px. */
const MIN_LABEL_GAP = 52;
/** The tinted run-up on the track before the next point. */
const LEAD_SECONDS = 15 * 60;

/** Left/right inset of the track, in px. */
const PAD = 20;
/** A broker day. Fixed offset, no daylight saving, so exactly 24 hours. */
const DAY_SECONDS = 86_400;
/** Height of a label chip, in px. */
const CHIP_H = 18;
/** Label lanes: distance of each chip's centre from the track, nearest first, above (-) then below (+). */
const LANES = [-24, 24, -48, 48];
/** Chip chrome around the two texts: colour stripe, gaps and end padding, in px. */
const CHIP_CHROME = 26;

/**
 * The time points on a timeline of today, midnight to midnight on the BROKER
 * clock.
 *
 * Read left to right like a chart's time axis. A glowing green line is now;
 * the track is filled up to it, and the fifteen minutes before the next point
 * are tinted in that point's colour, so the run-up is visible before the
 * countdown says so. Faint hourly gridlines, labelled every three hours, and
 * keep the clock readable without reading any labels.
 *
 * Each occurrence is a dot on the track with a chip on a stem: name and
 * countdown, or the time it fired if it is in the past (hollow and dimmed).
 * Chips take the nearest free lane above or below so they never overlap;
 * one that cannot fit anywhere leaves just its dot, with the name on hover.
 */
export function PointsTimeline({
  points,
  clockNow,
  leaderId,
  urgent,
  describe,
  nowLabel,
  formatUntil,
  offsetSec,
}: PointsTimelineProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(900);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setWidth(el.clientWidth));
    ro.observe(el);
    setWidth(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  /*
   * Today on the broker clock, midnight to midnight.
   *
   * Found by truncating in the shifted frame rather than with calendar
   * arithmetic: the broker runs at a fixed offset with no daylight saving, so
   * a broker day is exactly 24 hours and no hour ever exists twice.
   */
  const start = Math.floor((clockNow + offsetSec) / DAY_SECONDS) * DAY_SECONDS - offsetSec;
  const end = start + DAY_SECONDS;
  const pxPerHour = ((width - 2 * PAD) * 3600) / (end - start);
  // Label every hour if there is room, else every 2, 3, 4, 6 or 12.
  const labelStep = [1, 2, 3, 4, 6, 12].find((n) => n * pxPerHour >= MIN_LABEL_GAP) ?? 12;
  const xOf = (time: number) => PAD + ((width - 2 * PAD) * (time - start)) / (end - start);
  const nowX = xOf(clockNow);

  // Hour lines on the broker clock. `start` is already a broker midnight, so
  // the hours land on exact multiples of an hour from it.
  const hours: { time: number; label: boolean; midnight: boolean }[] = [];
  for (let time = start + 3600; time < end; time += 3600) {
    const h = Math.round(((time + offsetSec) % DAY_SECONDS) / 3600);
    hours.push({ time, label: h % labelStep === 0, midnight: false });
  }

  const leader = points.find((p) => p.id === leaderId) ?? null;

  const items = points
    .flatMap((p) => {
      const out: { key: string; point: TimelinePoint; time: number; past: boolean }[] = [];
      if (p.prevTime !== null && p.prevTime >= start && p.prevTime < clockNow) {
        out.push({ key: `${p.id}:prev`, point: p, time: p.prevTime, past: true });
      }
      if (p.nextTime >= clockNow && p.nextTime <= end) {
        out.push({ key: `${p.id}:next`, point: p, time: p.nextTime, past: false });
      }
      return out;
    })
    .sort((a, b) => a.time - b.time);

  /*
   * Lay chips out by priority, each taking the nearest free lane: the leader
   * first, so it always sits right by the track; then what is coming, soonest
   * first; then the past, most recent first, only where room is left.
   */
  const laneEnds = LANES.map(() => [] as [number, number][]);
  const rank = (it: (typeof items)[number]) =>
    !it.past && it.point.id === leaderId ? 0 : it.past ? 2 : 1;
  const order = [...items].sort(
    (a, b) => rank(a) - rank(b) || (a.past ? b.time - a.time : a.time - b.time),
  );
  const placed = new Map<string, { lane: number; left: number; w: number }>();
  for (const it of order) {
    const sub = it.past ? clockOf(it.time, offsetSec) : formatUntil(it.time - clockNow);
    const bold = rank(it) === 0;
    const w = Math.min(
      Math.ceil(textWidth(it.point.name, bold) + textWidth(sub, bold)) + CHIP_CHROME,
      width - 2 * PAD,
    );
    const x = xOf(it.time);
    const left = Math.max(0, Math.min(width - w, x - w / 2));
    const lane = laneEnds.findIndex((spans) => spans.every(([l, r]) => left > r + 4 || left + w < l - 4));
    if (lane === -1) continue;
    laneEnds[lane]!.push([left, left + w]);
    placed.set(it.key, { lane, left, w });
  }

  return (
    <div ref={ref} className="relative h-full min-h-0 w-full overflow-hidden select-none">
      {/* hour grid */}
      {hours.map((h) => (
        <div key={h.time}>
          <div
            className={cn(
              'absolute top-5 bottom-4 w-px',
              h.midnight ? 'border-s border-dashed border-muted-foreground/40' : h.label ? 'bg-border/70' : 'bg-border/30',
            )}
            style={{ left: xOf(h.time) }}
          />
          {(h.label || h.midnight) && (
            <span
              className={cn(
                'tabular absolute bottom-0.5 -translate-x-1/2 text-[9px]',
                h.midnight ? 'font-semibold text-muted-foreground' : 'text-muted-foreground/70',
              )}
              style={{ left: xOf(h.time) }}
            >
              {h.midnight ? weekdayOf(h.time, offsetSec) : clockOf(h.time, offsetSec)}
            </span>
          )}
        </div>
      ))}

      {/* the track: rounded, filled up to now */}
      <div
        className="absolute top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-muted"
        style={{ left: PAD, right: PAD }}
      />
      <div
        className="absolute top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-gradient-to-r from-transparent to-bull/50"
        style={{ left: PAD, width: Math.max(0, nowX - PAD) }}
      />
      {/* the run-up to the next point, in its colour */}
      {leader && leader.nextTime <= end && (
        <div
          className="absolute top-1/2 h-1.5 -translate-y-1/2 rounded-full"
          style={{
            left: xOf(Math.max(clockNow, leader.nextTime - LEAD_SECONDS)),
            width: xOf(leader.nextTime) - xOf(Math.max(clockNow, leader.nextTime - LEAD_SECONDS)),
            backgroundColor: leader.color,
            opacity: 0.55,
          }}
        />
      )}

      {/* now: a glowing line, behind the points and chips so it never hides them */}
      <div
        className="pointer-events-none absolute top-5 bottom-4 w-0.5 -translate-x-1/2 rounded-full bg-bull shadow-[0_0_8px_var(--bull)]"
        style={{ left: nowX }}
      />

      {/* stems first, so dots and chips sit on top of them */}
      {items.map((it) => {
        const p = placed.get(it.key);
        if (!p) return null;
        const off = LANES[p.lane]!;
        const x = xOf(it.time);
        const top = off < 0 ? `calc(50% + ${off + CHIP_H / 2}px)` : 'calc(50% + 3px)';
        const height = Math.abs(off) - CHIP_H / 2 - 3;
        return (
          <div
            key={`${it.key}:stem`}
            className={cn('absolute w-px', it.past ? 'bg-border' : 'bg-muted-foreground/40')}
            style={{ left: x, top, height }}
          />
        );
      })}

      {/* dots */}
      {items.map((it) => {
        const isLeader = !it.past && it.point.id === leaderId;
        return (
          <span
            key={`${it.key}:dot`}
            title={`${it.point.name} · ${describe(it.point)}`}
            className={cn(
              'absolute top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full',
              isLeader ? 'size-3.5 ring-[3px] ring-background' : 'size-2.5 ring-2 ring-background',
              isLeader && urgent && 'animate-pulse',
            )}
            style={
              it.past
                ? { left: xOf(it.time), backgroundColor: 'var(--background)', boxShadow: `inset 0 0 0 2px ${it.point.color}`, opacity: 0.6 }
                : { left: xOf(it.time), backgroundColor: it.point.color }
            }
          />
        );
      })}

      {/* chips */}
      {items.map((it) => {
        const p = placed.get(it.key);
        if (!p) return null;
        const isLeader = !it.past && it.point.id === leaderId;
        const off = LANES[p.lane]!;
        return (
          <div
            key={`${it.key}:chip`}
            title={`${it.point.name} · ${describe(it.point)}`}
            className={cn(
              'absolute flex items-center gap-1.5 overflow-hidden rounded-md border ps-0 pe-1.5 text-[10.5px] leading-none whitespace-nowrap shadow-sm',
              isLeader ? 'bg-elevated' : 'border-border/70 bg-surface',
              it.past && 'opacity-50',
            )}
            style={{
              left: p.left,
              width: p.w,
              height: CHIP_H,
              top: `calc(50% + ${off - CHIP_H / 2}px)`,
              ...(isLeader ? { borderColor: it.point.color } : {}),
            }}
          >
            <span className="h-full w-1 shrink-0" style={{ backgroundColor: it.point.color }} />
            <span className={cn('min-w-0 truncate', isLeader ? 'font-semibold text-foreground' : 'text-foreground/85')}>
              {it.point.name}
            </span>
            <span className={cn('tabular ms-auto shrink-0', isLeader ? 'font-semibold' : 'text-muted-foreground')}>
              {it.past ? clockOf(it.time, offsetSec) : formatUntil(it.time - clockNow)}
            </span>
          </div>
        );
      })}

      {/* now: its label, on top of everything */}
      <span
        className="tabular pointer-events-none absolute top-0.5 -translate-x-1/2 rounded-full bg-bull px-1.5 py-0.5 text-[9.5px] leading-none font-semibold whitespace-nowrap text-white shadow-sm"
        // Kept inside the bar near midnight, at either edge of the day.
        style={{ left: Math.min(Math.max(nowX, 42), width - 42) }}
      >
        {nowLabel} {clockOf(clockNow, offsetSec)}
      </span>
    </div>
  );
}

/**
 * HH:MM, 24-hour, on the broker clock — compact enough for a crowded axis.
 *
 * The offset is added into the value and the result read as UTC, which yields
 * the broker wall clock without needing a timezone name for it.
 */
function clockOf(sec: number, offsetSec: number): string {
  return new Date((sec + offsetSec) * 1000).toLocaleTimeString([], {
    timeZone: 'UTC',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/*
 * Real text widths for laying chips out, measured once per string on a shared
 * canvas in the page's own font. Estimating from character counts cut names
 * short: "COMEX Open" and "NY Open" are very different widths per letter.
 */
let measureCtx: CanvasRenderingContext2D | null = null;
const widthCache = new Map<string, number>();
function textWidth(text: string, bold: boolean): number {
  const key = `${bold ? 'b' : 'r'}${text}`;
  const hit = widthCache.get(key);
  if (hit !== undefined) return hit;
  measureCtx ??= document.createElement('canvas').getContext('2d');
  if (!measureCtx) return text.length * 6.2;
  const family = getComputedStyle(document.body).fontFamily || 'system-ui, sans-serif';
  measureCtx.font = `${bold ? 600 : 400} 10.5px ${family}`;
  const w = measureCtx.measureText(text).width;
  widthCache.set(key, w);
  return w;
}

/** Short weekday on the broker clock, for midnight markers. */
function weekdayOf(sec: number, offsetSec: number): string {
  return new Date((sec + offsetSec) * 1000).toLocaleDateString([], {
    timeZone: 'UTC',
    weekday: 'short',
  });
}
